import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { Shape } from "shared/datamodel/schemas/shape";
import { StickyNote } from "shared/datamodel/schemas/sticky-note";
import { userAtom } from "state-atoms";
import { useDynamicIntegrations } from "./use-dynamic-integrations";

export enum DataLayerErrorType {
  GUEST = "GUEST",
  MISSING_DATA = "MISSING_DATA",
}

export function useDataLayerErrors(elementId: string, element: Shape | StickyNote): DataLayerErrorType | null {
  const user = useAtomValue(userAtom);
  const { isLoading, integrationItemIds, integrationsConfig, getDisplayedColumnIds } = useDynamicIntegrations(
    elementId,
    element
  );

  // Guest state has priority - if user is a guest, show guest badge
  const isGuest = useMemo(() => {
    return !user || user.isGuest;
  }, [user]);

  const hasMissingItems = useMemo(() => {
    return (
      Object.entries(integrationItemIds).flatMap(([integrationId, itemIds]) => {
        const integration = integrationsConfig[integrationId];
        return itemIds.filter(
          (itemId) => !integration?.data?.items?.some((item: { id: string }) => item.id === itemId)
        );
      }).length > 0
    );
  }, [integrationItemIds, integrationsConfig]);

  const hasMisingColumns = useMemo(() => {
    return (
      Object.keys(integrationItemIds).flatMap((integrationId) => {
        const integration = integrationsConfig[integrationId];
        const columns = integration?.columns ?? [];
        const displayedColumnIds = getDisplayedColumnIds();
        return displayedColumnIds.filter(
          (columnId) => !columns.some((column: { id: string }) => column.id === columnId)
        );
      }).length > 0
    );
  }, [getDisplayedColumnIds, integrationItemIds, integrationsConfig]);

  const ruleHasMissingColumn = useMemo(() => {
    const ruleFields = element.dataLayer?.rules?.map((rule) => rule.fieldId.split(":")[3]) ?? [];

    return (
      Object.keys(integrationItemIds).flatMap((integrationId) => {
        const integration = integrationsConfig[integrationId];
        const columns = integration?.columns ?? [];
        const columnIds = new Set(columns.map((column: { id: string }) => column.id));
        return ruleFields.filter((ruleField) => !columnIds.has(ruleField));
      }).length > 0
    );
  }, [element.dataLayer?.rules, integrationItemIds, integrationsConfig]);

  const hasMissingData = useMemo(() => {
    if (isLoading) {
      return false;
    }
    return hasMisingColumns || hasMissingItems || ruleHasMissingColumn;
  }, [isLoading, hasMisingColumns, hasMissingItems, ruleHasMissingColumn]);

  // Priority: GUEST > MISSING_DATA > NULL
  if (isGuest) {
    return DataLayerErrorType.GUEST;
  }

  if (hasMissingData) {
    return DataLayerErrorType.MISSING_DATA;
  }

  return null;
}
